import {
  type ApprovalDecision,
  type ClarificationAnswer,
  type EditedEmailContent,
} from '../interfaces/chat.interfaces';

/**
 * The request body that resumes a graph suspended on a commit-approval
 * `interrupt()`. The plan-bound approval token rides in the `resume` channel
 * ONLY — never as message text — so the server can `Command({ resume })` the
 * chat session's durable thread without the token (or any new turn) ever
 * reaching the LLM or `chat_history`.
 */
export interface ApprovalResumeBody {
  resume: {
    approvalToken: string;
    editedEmailContent?: EditedEmailContent;
  };
}

/**
 * Builds the `resume` body for a Confirm. Sent via
 * `chat.sendMessage(undefined, { body })` so NO new user message (HumanMessage)
 * is appended — the resume continues the SAME chat session/transport.
 */
export function buildApprovalResumeBody(
  decision: ApprovalDecision,
): ApprovalResumeBody {
  return {
    resume: {
      approvalToken: decision.approvalToken,
      ...(decision.editedEmailContent
        ? { editedEmailContent: decision.editedEmailContent }
        : {}),
    },
  };
}

/** The `resume` body for a clarification answer (generate_question interrupt). */
export interface ClarificationResumeBody {
  resume: { clarificationAnswer: string };
}

export function buildClarificationResumeBody(
  answer: string,
): ClarificationResumeBody {
  return { resume: { clarificationAnswer: answer } };
}

/**
 * The `resume` body for a click on a navigation link.
 *
 * The odd one of the three: `frontend_navigate` suspends nothing, so this
 * normally lands on a thread with no interrupt to continue. It rides the channel
 * all the same, because it is what tells the server to record the card as
 * decided (a reload then renders it settled, not re-armed); the decision itself
 * reaches the agent as the request's ordinary user message.
 */
export interface NavigateResumeBody {
  resume: { navigateDecision: string };
}

export function buildNavigateResumeBody(decision: string): NavigateResumeBody {
  return { resume: { navigateDecision: decision } };
}

/**
 * Any card-choice body — the discriminated `resume` channel a human's click goes
 * out on. Each variant carries exactly one field; the server reads only the one
 * matching the card, and continues a suspended graph with it only when one is
 * actually waiting.
 */
type ResumeBody =
  | ApprovalResumeBody
  | ClarificationResumeBody
  | NavigateResumeBody;

/**
 * One click on a card, as it goes out: the `resume` body, plus the message the
 * request carries.
 *
 * The message half is not decoration. A card that SUSPENDED the turn is resumed
 * with `Command({ resume })`, and its `text` never reaches the model — so what
 * goes there only has to be a faithful bubble. A card that suspended nothing —
 * the navigation link, rendered beside a delivered answer — has no run to
 * continue, so the server reads the request as the ordinary turn it is and
 * `text` is EXACTLY what the agent answers. Hence the two fields, and hence the
 * navigation link sending its full outcome sentence where the other two send
 * their label.
 */
export interface CardChoice {
  readonly body: ResumeBody;
  /** The user message. What the agent reads, when no run is suspended. */
  readonly text: string;
  /** The bubble the user sees, whatever the text. */
  readonly displayLabel: string;
}

/** A Confirm on an approval gate — the label is all the model could ever see. */
export function approvalChoice(decision: ApprovalDecision): CardChoice {
  const label = decision.label ?? 'Confirmé';

  return {
    body: buildApprovalResumeBody(decision),
    text: label,
    displayLabel: label,
  };
}

/** A picked (or typed) clarification option: its `value` resumes, its label shows. */
export function clarificationChoice(answer: ClarificationAnswer): CardChoice {
  return {
    body: buildClarificationResumeBody(answer.value),
    text: answer.label,
    displayLabel: answer.label,
  };
}

/**
 * A click on a navigation link — the one choice whose text the agent reads.
 *
 * `value` is the outcome sentence the card composed ("j'ai ouvert la page « X »",
 * or the failure and its reason), so the next turn starts from what actually
 * happened; `label` stays the short button bubble.
 */
export function navigateChoice(decision: ClarificationAnswer): CardChoice {
  return {
    body: buildNavigateResumeBody(decision.value),
    text: decision.value,
    displayLabel: decision.label,
  };
}
